import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  CardActions,
  Typography,
  CardContent,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  Login,
  TextInput,
  PasswordInput,
  required,
  useLogin,
  useNotify,
  useTranslate,
  Form,
} from 'react-admin';
import { Link } from 'react-router-dom';
import { RecaptchaInput, RecaptchaInputHandle } from './RecaptchaInput';

export const LoginForm = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const recaptchaRef = React.useRef<RecaptchaInputHandle>(null);
  const queryClient = useQueryClient();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  const submit = (values: any) => {
    setError(null);
    setLoading(true);
    // Clear the React Query cache before exchanging credentials. This ensures the
    // dashboard mounts with empty ['auth','getIdentity'] and ['auth','canAccess',...]
    // caches, preventing stale user data from previous sessions from rendering.
    // Note: useLogin only invalidates getPermissions; we must clear everything here.
    queryClient.clear();
    login(values)
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        recaptchaRef.current?.reset();
        const message =
          typeof err === 'string'
            ? err
            : err?.message || translate('ra.auth.sign_in_error');
        setError(message);
        notify(message, { type: 'error' });
      });
  };

  return (
    <Login
      sx={{
        // Default RaLogin card is minWidth 300 + overflow hidden; normal
        // reCAPTCHA is 304px and gets clipped on phones. Let the card flex.
        '& .RaLogin-card': {
          overflow: 'visible',
          minWidth: { xs: 0, sm: 300 },
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
          maxWidth: 400,
        },
      }}
    >
      <Form onSubmit={submit}>
        <CardContent sx={{ paddingBottom: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextInput
            autoFocus
            source="email"
            label="Email"
            autoComplete="email"
            type="email"
            validate={required()}
          />
          <PasswordInput
            source="password"
            label="Password"
            autoComplete="current-password"
            validate={required()}
          />
          {siteKey && (
            <RecaptchaInput
              ref={recaptchaRef}
              source="recaptchaToken"
              siteKey={siteKey}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              sx={{ position: 'relative' }}
            >
              {loading && (
                <CircularProgress
                  size={19}
                  thickness={3}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-9.5px',
                  }}
                />
              )}
              <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
                {translate('ra.auth.sign_in')}
              </span>
            </Button>
          </Box>
        </CardContent>
      </Form>
      <CardActions sx={{ justifyContent: 'center' }}>
        <Typography component={Link} to="/reset-password" variant="caption">
          Forgot your password?
        </Typography>
      </CardActions>
    </Login>
  );
};
